import { motion } from "framer-motion"
import { Award, Heart, Lightbulb, Shield, Users, TrendingUp, CheckCircle2 } from "lucide-react"
import { fadeInUp, staggerContainer, staggerItem, slideInRight } from "@/lib/motion"
import { StatCard } from "@/components/Cards"
import { IMAGES } from "@/assets/images"
import type { StatItem } from "@/lib/index"

const stats: StatItem[] = [
  { value: "20+", labelAr: "سنوات من الخبرة", icon: "calendar" },
  { value: "251+", labelAr: "عميل راضٍ", icon: "users" },
  { value: "2468+", labelAr: "فعالية ناجحة", icon: "star" },
  { value: "50+", labelAr: "منتج يومياً", icon: "package" },
]

const values = [
  {
    id: "quality",
    icon: Award,
    titleAr: "الجودة",
    descriptionAr: "نستخدم أجود المكونات ونلتزم بأعلى معايير الجودة في كل منتج نصنعه",
  },
  {
    id: "commitment",
    icon: Shield,
    titleAr: "الالتزام",
    descriptionAr: "نلتزم بمواعيد التسليم ونضمن وصول منتجاتنا طازجة في الوقت المحدد",
  },
  {
    id: "innovation",
    icon: Lightbulb,
    titleAr: "الابتكار",
    descriptionAr: "نطور منتجاتنا باستمرار ونبتكر حلولاً جديدة تلبي احتياجات عملائنا",
  },
  {
    id: "hospitality",
    icon: Heart,
    titleAr: "الضيافة",
    descriptionAr: "نصنع كل منتج بحب وشغف لنقدم تجربة ضيافة استثنائية لضيوفكم",
  },
]

const whyTrustUs = [
  {
    id: "experience",
    titleAr: "خبرة سعودية متخصصة",
    descriptionAr: "معمل متخصص في الكيترنج والمخبوزات منذ أكثر من 15 عاماً في السوق السعودي",
  },
  {
    id: "scale",
    titleAr: "قدرة إنتاجية عالية",
    descriptionAr: "نخدم الشركات الكبرى والفعاليات الضخمة بكميات تصل إلى آلاف القطع يومياً",
  },
  {
    id: "consistency",
    titleAr: "ثبات في الجودة",
    descriptionAr: "نظام إنتاج احترافي يضمن نفس الجودة العالية في كل طلب وكل مرة",
  },
  {
    id: "team",
    titleAr: "فريق محترف",
    descriptionAr: "طهاة وخبازون ذوو خبرة عالمية ومدربون على أعلى المعايير",
  },
]

export default function About() {
  return (
    <div className="min-h-screen" dir="rtl">
        <section className="relative h-[60vh] min-h-[500px] flex items-center justify-center overflow-hidden">
          <div className="absolute inset-0 z-0">
            <img
              src={IMAGES.HOSPITALITY_TABLE_10}
              alt="أطياف الخبازين"
              className="w-full h-full object-cover opacity-30"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-background/50 via-transparent to-background/70" />
          </div>

          <motion.div
            className="relative z-10 container mx-auto px-4 text-center"
            initial="hidden"
            animate="visible"
            variants={fadeInUp}
          >
            <motion.div
              className="inline-block mb-6"
              initial={{ scale: 0, rotate: -180 }}
              animate={{ scale: 1, rotate: 0 }}
              transition={{ type: "spring", stiffness: 200, damping: 20, delay: 0.2 }}
            >
              <div className="w-20 h-1 bg-accent mx-auto" />
            </motion.div>
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold text-foreground mb-6">
              قصتنا في كل لقمة
            </h1>
            <p className="text-xl md:text-2xl text-muted-foreground max-w-3xl mx-auto">
              رحلة من الشغف والإتقان في صناعة الضيافة السعودية
            </p>
          </motion.div>
        </section>

        <section className="py-16 bg-muted/30">
          <div className="container mx-auto px-4">
            <motion.div
              className="grid grid-cols-2 md:grid-cols-4 gap-6"
              variants={staggerContainer}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-100px" }}
            >
              {stats.map((stat) => (
                <motion.div key={stat.labelAr} variants={staggerItem}>
                  <StatCard stat={stat} />
                </motion.div>
              ))}
            </motion.div>
          </div>
        </section>

        <section className="py-20 lg:py-32">
          <div className="container mx-auto px-4">
            <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
              <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-100px" }}
                variants={fadeInUp}
              >
                <div className="inline-block mb-4">
                  <span className="text-accent font-semibold text-lg">من نحن</span>
                </div>
                <h2 className="text-4xl md:text-5xl font-bold text-foreground mb-6">
                  معمل متخصص في صناعة الضيافة
                </h2>
                <div className="space-y-4 text-lg text-muted-foreground">
                  <p>
                    بدأت رحلتنا من شغف حقيقي بصناعة المخبوزات والحلويات الفاخرة. اليوم، نحن معمل
                    متخصص يخدم أكبر الشركات والفعاليات في المملكة العربية السعودية.
                  </p>
                  <p>
                    نجمع بين الخبرة السعودية الأصيلة والمعايير العالمية في الإنتاج، لنقدم منتجات
                    تليق بضيوفكم وتعكس مكانة علامتكم التجارية.
                  </p>
                  <p>
                    من كيترنج الشركات إلى الأعراس الفاخرة، من توريد الكافيهات إلى المخبوزات
                    اليومية - نحن شريككم الموثوق في كل مناسبة.
                  </p>
                </div>

                <div className="mt-8 space-y-3">
                  {[
                    "معمل إنتاج حديث بأعلى معايير الصحة والسلامة",
                    "فريق من الطهاة والخبازين ذوي الخبرة العالمية",
                    "قدرة على خدمة الفعاليات الكبرى والطلبات الضخمة",
                    "نظام توريد يومي يضمن الطازجية والجودة",
                  ].map((item, index) => (
                    <motion.div
                      key={index}
                      className="flex items-start gap-3"
                      initial={{ opacity: 0, x: -20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: index * 0.1 }}
                    >
                      <CheckCircle2 className="w-6 h-6 text-accent flex-shrink-0 mt-1" />
                      <span className="text-foreground">{item}</span>
                    </motion.div>
                  ))}
                </div>
              </motion.div>

              <motion.div
                className="relative"
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-100px" }}
                variants={slideInRight}
              >
                <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                  <img
                    src={IMAGES.HOSPITALITY_TABLE_3}
                    alt="معمل أطياف الخبازين"
                    className="w-full h-[500px] object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-primary/20 to-transparent" />
                </div>
                <div className="absolute -bottom-6 -right-6 w-48 h-48 bg-accent/10 rounded-full blur-3xl" />
                <div className="absolute -top-6 -left-6 w-48 h-48 bg-primary/10 rounded-full blur-3xl" />
              </motion.div>
            </div>
          </div>
        </section>

        <section className="py-20 lg:py-32 bg-gradient-to-b from-muted/30 to-background">
          <div className="container mx-auto px-4">
            <motion.div
              className="text-center mb-16"
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-100px" }}
              variants={fadeInUp}
            >
              <div className="inline-block mb-4">
                <span className="text-accent font-semibold text-lg">قيمنا</span>
              </div>
              <h2 className="text-4xl md:text-5xl font-bold text-foreground mb-6">
                ما يميزنا ويحركنا
              </h2>
              <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
                قيم راسخة نلتزم بها في كل منتج نصنعه وكل عميل نخدمه
              </p>
            </motion.div>

            <motion.div
              className="grid md:grid-cols-2 lg:grid-cols-4 gap-8"
              variants={staggerContainer}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-100px" }}
            >
              {values.map((value) => {
                const Icon = value.icon
                return (
                  <motion.div
                    key={value.id}
                    variants={staggerItem}
                    className="group"
                  >
                    <div className="bg-card rounded-2xl p-8 h-full border border-border hover:border-accent transition-all duration-500 hover:shadow-lg">
                      <div className="w-16 h-16 rounded-xl bg-accent/10 flex items-center justify-center mb-6 group-hover:bg-accent/20 transition-colors duration-500">
                        <Icon className="w-8 h-8 text-accent" />
                      </div>
                      <h3 className="text-2xl font-bold text-foreground mb-4">
                        {value.titleAr}
                      </h3>
                      <p className="text-muted-foreground leading-relaxed">
                        {value.descriptionAr}
                      </p>
                    </div>
                  </motion.div>
                )
              })}
            </motion.div>
          </div>
        </section>

        <section className="py-20 lg:py-32">
          <div className="container mx-auto px-4">
            <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
              <motion.div
                className="relative order-2 lg:order-1"
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-100px" }}
                variants={fadeInUp}
              >
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-4">
                    <img
                      src="/images/WhatsApp Image 2026-05-05 at 1.31.09 PM (1).jpeg"
                      alt="مخبوزات أطياف الخبازين"
                      className="w-full h-48 object-cover rounded-xl"
                    />
                    <img
                      src={IMAGES.COFFEE_POUR_4}
                      alt="قهوة أطياف الخبازين"
                      className="w-full h-64 object-cover rounded-xl"
                    />
                  </div>
                  <div className="space-y-4 pt-8">
                    <img
                      src={IMAGES.HOSPITALITY_TABLE_3}
                      alt="ضيافة أطياف الخبازين"
                      className="w-full h-64 object-cover rounded-xl"
                    />
                    <img
                      src="/images/atiyaf_full_collection_1 (1)_page-0001.jpg"
                      alt="منتجات أطياف الخبازين"
                      className="w-full h-48 object-cover rounded-xl"
                    />
                  </div>
                </div>
              </motion.div>

              <motion.div
                className="order-1 lg:order-2"
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-100px" }}
                variants={slideInRight}
              >
                <div className="inline-block mb-4">
                  <span className="text-accent font-semibold text-lg">لماذا تثق بنا</span>
                </div>
                <h2 className="text-4xl md:text-5xl font-bold text-foreground mb-8">
                  شريك موثوق لأكبر الشركات
                </h2>

                <div className="space-y-6">
                  {whyTrustUs.map((item, index) => (
                    <motion.div
                      key={item.id}
                      className="flex gap-4"
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: index * 0.1 }}
                    >
                      <div className="flex-shrink-0">
                        <div className="w-12 h-12 rounded-full bg-accent/10 flex items-center justify-center">
                          <TrendingUp className="w-6 h-6 text-accent" />
                        </div>
                      </div>
                      <div>
                        <h3 className="text-xl font-bold text-foreground mb-2">
                          {item.titleAr}
                        </h3>
                        <p className="text-muted-foreground leading-relaxed">
                          {item.descriptionAr}
                        </p>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        <section className="py-20 lg:py-32 bg-gradient-to-b from-background to-muted/30">
          <div className="container mx-auto px-4">
            <motion.div
              className="text-center mb-16"
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-100px" }}
              variants={fadeInUp}
            >
              <div className="inline-block mb-4">
                <span className="text-accent font-semibold text-lg">فريقنا</span>
              </div>
              <h2 className="text-4xl md:text-5xl font-bold text-foreground mb-6">
                خبراء في صناعة الضيافة
              </h2>
              <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
                فريق من الطهاة والخبازين المحترفين يعملون بشغف لتقديم أفضل تجربة ضيافة
              </p>
            </motion.div>

            <motion.div
              className="grid md:grid-cols-3 gap-8 max-w-4xl mx-auto"
              variants={staggerContainer}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-100px" }}
            >
              {[
                { icon: Users, titleAr: "فريق الإنتاج", countAr: "50+ محترف" },
                { icon: Award, titleAr: "الطهاة والخبازون", countAr: "20+ خبير" },
                { icon: Shield, titleAr: "مراقبة الجودة", countAr: "فريق متخصص" },
              ].map((team, index) => {
                const Icon = team.icon
                return (
                  <motion.div
                    key={index}
                    variants={staggerItem}
                    className="text-center"
                  >
                    <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-accent/10 mb-6">
                      <Icon className="w-10 h-10 text-accent" />
                    </div>
                    <h3 className="text-2xl font-bold text-foreground mb-2">
                      {team.titleAr}
                    </h3>
                    <p className="text-lg text-muted-foreground">{team.countAr}</p>
                  </motion.div>
                )
              })}
            </motion.div>
          </div>
        </section>

        <section className="py-20 lg:py-32">
          <div className="container mx-auto px-4">
            <motion.div
              className="bg-gradient-to-br from-primary to-primary/80 rounded-3xl p-12 lg:p-16 text-center relative overflow-hidden"
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-100px" }}
              variants={fadeInUp}
            >
              <div className="absolute inset-0 opacity-10">
                <div className="absolute top-0 left-0 w-64 h-64 bg-accent rounded-full blur-3xl" />
                <div className="absolute bottom-0 right-0 w-64 h-64 bg-accent rounded-full blur-3xl" />
              </div>
              <div className="relative z-10">
                <h2 className="text-4xl md:text-5xl font-bold text-primary-foreground mb-6">
                  جاهزون لخدمتكم
                </h2>
                <p className="text-xl text-primary-foreground/90 mb-8 max-w-2xl mx-auto">
                  دعونا نكون شريككم في صناعة تجربة ضيافة استثنائية لعملائكم وضيوفكم
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <a
                    href="#/contact"
                    className="inline-flex items-center justify-center px-8 py-4 bg-accent text-accent-foreground rounded-xl font-semibold text-lg hover:bg-accent/90 transition-all duration-300 hover:scale-105"
                  >
                    تواصل معنا
                  </a>
                  <a
                    href="https://wa.me/966550096297"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center justify-center px-8 py-4 bg-primary-foreground text-primary rounded-xl font-semibold text-lg hover:bg-primary-foreground/90 transition-all duration-300 hover:scale-105"
                  >
                    واتساب مباشر
                  </a>
                </div>
              </div>
            </motion.div>
          </div>
        </section>
      </div>
  )
}
